<template>
  <div class="repay-page">
    <van-nav-bar
      title="待还款"
      left-arrow
      safe-area-inset-top
      @click-left="$router.back()"
    />

    <div v-if="loading" class="loading-wrap">
      <van-loading type="spinner" color="#3f8aec">加载中...</van-loading>
    </div>

    <template v-else-if="list.length">
      <div class="summary">
        <div class="sum-title">待还款总金额(元)</div>
        <div class="sum-total">¥{{ money(totals.remain) }}</div>
        <div class="sum-grid">
          <div class="sum-item">
            <div class="lab">待还本金</div>
            <div class="val">¥{{ money(totals.principal) }}</div>
          </div>
          <div class="sum-item">
            <div class="lab">待还利息</div>
            <div class="val">¥{{ money(totals.interest) }}</div>
          </div>
          <div class="sum-item">
            <div class="lab">待还违约金</div>
            <div class="val warn">¥{{ money(totals.penalty) }}</div>
          </div>
        </div>
      </div>

      <div class="section-title">待还账单明细</div>
      <div
        v-for="item in list"
        :key="item.id"
        class="bill-card"
      >
        <div class="bill-head">
          <span>第{{ item.period }}期 · {{ item.product_name || item.contract_no }}</span>
          <van-tag v-if="item.overdue_days > 0" type="danger" plain>逾期{{ item.overdue_days }}天</van-tag>
          <van-tag v-else type="primary" plain>待还</van-tag>
        </div>
        <div class="bill-amount">¥{{ money(remainOf(item)) }}</div>
        <div class="bill-rows">
          <span>本金 ¥{{ money(shareOf(item, 'principal_amount')) }}</span>
          <span>利息 ¥{{ money(shareOf(item, 'interest_amount')) }}</span>
          <span>违约金 ¥{{ money(shareOf(item, 'penalty_amount')) }}</span>
        </div>
        <div class="bill-due">应还日 {{ item.due_date || '-' }}</div>
      </div>

      <div class="section-title">还款渠道选择</div>
      <div class="channel-box">
        <van-radio-group v-model="channel">
          <van-cell-group :border="false">
            <van-cell
              v-for="c in channels"
              :key="c.code"
              :title="c.name"
              :label="channelLabel(c)"
              clickable
              @click="channel = c.code"
            >
              <template #right-icon>
                <van-radio :name="c.code" />
              </template>
            </van-cell>
          </van-cell-group>
        </van-radio-group>
        <div v-if="!channels.length" class="empty-tip">暂无可用渠道，请联系管理员配置</div>
      </div>

      <div class="footer-space" />
      <div class="footer">
        <div class="pay-info">
          <span>应付</span>
          <b>¥{{ money(totals.remain) }}</b>
        </div>
        <van-button
          round
          color="linear-gradient(to right,#6aa8f5, #3d8aef)"
          :loading="paying"
          :disabled="!list.length || !channel"
          @click="confirmPay"
        >确认还款</van-button>
      </div>
    </template>

    <div v-else class="empty">
      <img src="../assets/img/zw.png" alt="" />
      <p>暂无待还账单</p>
      <van-button round type="info" size="small" @click="$router.replace('/home')">返回首页</van-button>
    </div>
  </div>
</template>

<script>
import { Toast } from 'vant'
import { bills, payChannels, repayAll } from '@/api/request.js'

export default {
  name: 'Repay',
  data() {
    return {
      loading: true,
      list: [],
      channels: [],
      channel: '',
      paying: false
    }
  },
  computed: {
    totals() {
      return this.list.reduce(
        (acc, b) => {
          acc.principal += this.shareOf(b, 'principal_amount')
          acc.interest += this.shareOf(b, 'interest_amount')
          acc.penalty += this.shareOf(b, 'penalty_amount')
          acc.remain += this.remainOf(b)
          return acc
        },
        { principal: 0, interest: 0, penalty: 0, remain: 0 }
      )
    }
  },
  methods: {
    money(n) {
      return Number(n || 0).toFixed(2)
    },
    remainOf(b) {
      return Math.max(0, Number((Number(b.amount || 0) - Number(b.paid_amount || 0)).toFixed(2)))
    },
    /** 按剩余应还比例分摊展示本金/利息/违约金 */
    shareOf(b, key) {
      const amount = Number(b.amount || 0)
      const paid = Number(b.paid_amount || 0)
      const part = Number(b[key] || 0)
      if (amount <= 0) return 0
      const ratio = Math.min(1, Math.max(0, (amount - paid) / amount))
      return Number((part * ratio).toFixed(2))
    },
    channelHint(code) {
      if (code === 'bank' || String(code || '').startsWith('bank')) return '银行卡转账'
      if (code === 'wechat' || code === 'alipay') return '扫码支付'
      return ''
    },
    channelLabel(c) {
      const d = (c && c.display) || {}
      if (d.type === 'bank' || c.mode === 'bank') {
        const parts = []
        if (d.bankName) parts.push(d.bankName)
        if (d.accountName) parts.push(`户名 ${d.accountName}`)
        if (d.accountNo) parts.push(d.accountNo)
        if (d.branch) parts.push(d.branch)
        if (parts.length) return parts.join(' · ')
      }
      if (d.type === 'qr' && d.payeeName) return `收款人 ${d.payeeName}`
      return c.remark || this.channelHint(c.code)
    },
    async load() {
      this.loading = true
      try {
        const [billRes, chRes] = await Promise.all([bills('pending'), payChannels()])
        this.list = billRes.data || []
        const data = chRes.data || {}
        this.channels = data.list || []
        this.channel = data.defaultChannel || (this.channels[0] && this.channels[0].code) || ''
      } finally {
        this.loading = false
      }
    },
    async confirmPay() {
      if (!this.list.length) {
        Toast('暂无待还账单')
        return
      }
      if (!this.channel) {
        Toast('请选择还款渠道')
        return
      }

      this.paying = true
      try {
        const res = await repayAll({ channel: this.channel })
        const data = res.data || {}
        if (data.payUrl) {
          // 相对路径 /api/... 走当前域名（nginx 反代），勿写死 localhost
          window.location.href = data.payUrl
          return
        }
        Toast.success(data.message || '已提交')
        await this.load()
      } finally {
        this.paying = false
      }
    }
  },
  created() {
    this.load()
  }
}
</script>

<style lang="less" scoped>
.repay-page {
  min-height: 100%;
  background: #f3f6fb;
  padding-bottom: 90px;
}
/deep/ .van-nav-bar {
  background: linear-gradient(180deg, #6aa8f5 0%, #4b96f0 100%);
  .van-nav-bar__title,
  .van-icon {
    color: #fff;
  }
}
.loading-wrap {
  padding: 80px 0;
  text-align: center;
}
.summary {
  margin: 12px;
  background: linear-gradient(180deg, #4b96f0 0%, #3d8aef 100%);
  border-radius: 14px;
  padding: 18px 16px 14px;
  color: #fff;
  box-shadow: 0 8px 20px rgba(61, 138, 239, 0.28);
}
.sum-title {
  font-size: 13px;
  opacity: 0.92;
}
.sum-total {
  margin-top: 6px;
  font-size: 34px;
  font-weight: 700;
  color: #ff3b30;
  letter-spacing: 0.5px;
}
.sum-grid {
  margin-top: 14px;
  display: flex;
  background: rgba(255, 255, 255, 0.14);
  border-radius: 10px;
  overflow: hidden;
}
.sum-item {
  flex: 1;
  text-align: center;
  padding: 10px 4px;
  .lab {
    font-size: 11px;
    opacity: 0.9;
  }
  .val {
    margin-top: 4px;
    font-size: 13px;
    font-weight: 600;
  }
  .warn {
    color: #ffe08a;
  }
}
.section-title {
  margin: 14px 16px 8px;
  font-size: 14px;
  font-weight: 600;
  color: #333;
}
.bill-card {
  margin: 0 12px 10px;
  background: #fff;
  border-radius: 12px;
  padding: 12px 14px;
  box-shadow: 0 4px 12px rgba(31, 45, 61, 0.04);
}
.bill-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 13px;
  color: #333;
}
.bill-amount {
  margin-top: 6px;
  font-size: 22px;
  font-weight: 700;
  color: #ff3b30;
}
.bill-rows {
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px 12px;
  font-size: 12px;
  color: #666;
}
.bill-due {
  margin-top: 6px;
  font-size: 12px;
  color: #999;
}
.channel-box {
  margin: 0 12px;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(31, 45, 61, 0.04);
}
.empty-tip {
  padding: 16px;
  text-align: center;
  color: #999;
  font-size: 13px;
}
.footer {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 16px calc(10px + env(safe-area-inset-bottom));
  background: #fff;
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.06);
  .pay-info {
    font-size: 13px;
    color: #666;
    b {
      margin-left: 6px;
      color: #ff3b30;
      font-size: 20px;
    }
  }
  .van-button {
    min-width: 140px;
  }
}
.footer-space {
  height: 20px;
}
.empty {
  padding-top: 80px;
  text-align: center;
  img {
    width: 120px;
  }
  p {
    margin: 12px 0 16px;
    color: #999;
    font-size: 14px;
  }
}
</style>
